import React, { useState } from "react";
import "./Login.css";
import GoogleAuth from "./GoogleAuth";
import FacebookAuth from "./FacebookAuth";
import AppleAuth from "./AppleAuth";
import { FloatingLabel, Form } from "react-bootstrap";
import { Formik } from "formik";
import * as yup from "yup";
import { ErrorsMsg, PLEASE_WAIT_TXT } from "../../utils/Constant";
import { appSendLoginOtp } from "../../services/AuthServices";
import { useSelector } from "react-redux";
import { LOGIN_FAIL } from "../../store/actions/types";
import {isMacOs, isIOS} from "react-device-detect";

/**
 * BOC
 * API Security - Step 4 OTP login / signup UI
 * date 23-10-2026
 * use_of_code: Login is OTP only (password login is disabled on the server). The customer enters the
 * e-mail / mobile number, the OTP is sent and the OTP screen is opened.
 */
const schema = yup.object().shape({
  login_id: yup.string().trim().required(ErrorsMsg.required),
});

function Login({ active, toggleAuthFormCb, setVerifyOTPResponse }) {
  const [msg, setMsg] = useState("");
  // API Security - Step 4 (26-10-2026): NOT_REGISTERED counter, highlights "Create new account" and restarts its animation
  const [notRegistered, setNotRegistered] = useState(0);

  // social login errors are returned with the LOGIN_FAIL id
  const error = useSelector((state) => state.error);
  const socialMsg = error.id === LOGIN_FAIL && typeof error.msg === "string" ? error.msg : "";

  if (!active) return null;

  const onSubmitSuccess = (values, { setSubmitting }) => {
    const login_id = values.login_id.trim();
    setMsg("");
    setNotRegistered(0);
    appSendLoginOtp({ login_id: login_id })
      .then((res) => {
        res = JSON.parse(res?.data);
        if (res?.status === "success") {
          setVerifyOTPResponse(res, login_id);
          toggleAuthFormCb("otplogin");
        } else {
          setMsg(res?.message || "Something went wrong. Please try again.");
          if (res?.error_code === "NOT_REGISTERED") {
            setNotRegistered((count) => count + 1);
          }
          setSubmitting(false);
        }
      })
      .catch(() => {
        setMsg("Something went wrong. Please try again.");
        setSubmitting(false);
      });
  };

  return (
    <>
      <Formik
        validationSchema={schema}
        onSubmit={onSubmitSuccess}
        initialValues={{
          login_id: "",
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleSubmit,
          isSubmitting,
        }) => (
          <Form onSubmit={handleSubmit} noValidate autoComplete="off">
            <FloatingLabel
              controlId="login_id"
              label="Email / Mobile Number"
              className="mb-3"
            >
              <Form.Control
                type="text"
                name="login_id"
                autoComplete="off"
                placeholder="Email / Mobile Number"
                value={values.login_id}
                onChange={handleChange}
                isInvalid={touched.login_id && errors.login_id}
              />

              <Form.Control.Feedback type="invalid">
                {errors.login_id}
              </Form.Control.Feedback>
            </FloatingLabel>

            {msg.length > 0 ? <span className="text-danger">{msg}</span> : null}
            {!msg && socialMsg ? <span className="text-danger">{socialMsg}</span> : null}

            <button
              className="btn btn-primary mt-35"
              type="submit"
              disabled={isSubmitting || !values.login_id.trim()}
            >
              {isSubmitting ? PLEASE_WAIT_TXT : "LOGIN WITH OTP"}
            </button>
          </Form>
        )}
      </Formik>

      <div className="social-login mt-40">
        <p>or login with</p>
        <ul className="mt-40 login-item-list">
          <li>
            <FacebookAuth />
          </li>
          <li>
            <GoogleAuth />
          </li>
          {(isMacOs || isIOS) ? <li><AppleAuth /></li> : ""}
        </ul>
        {notRegistered > 0 && <div className="signup-hint">New to Darveys? Create your account in a minute.</div>}
        <button
          key={notRegistered}
          className={"btn-blank text-underline mt-20" + (notRegistered > 0 ? " create-account-highlight" : "")}
          type="button"
          onClick={(e) => toggleAuthFormCb("signup")}
        >
          Create new account
        </button>
      </div>
    </>
  );
}
/**
 * EOC
 */
export default Login;
